import { Button } from "@shadcn/ui/button";
import { createFileRoute, Link, useLoaderData, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { usePreloadedQuery } from "react-relay";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { EmptyCard3 } from "@shadcn/pro-blocks/application/empty-sections/empty-section-4";
import { RecommendedProducts } from "~/components/ProductDrawer/RecommendedProducts";
import { ProductDrawer } from "~/components/ProductDrawer";
import { routeHubMetaNameQuery } from "../__generated__/routeHubMetaNameQuery.graphql";
import { hubMetaNameQuery } from "../route";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { useHubSession } from "@contexts/HubSessionContext";
import { useCart } from "~/contexts/CartContext";
import { formatToCurrency } from "@utils/currency";
import z from "zod";
import { MapPin } from "lucide-react";
import { loadQuery, graphql } from "react-relay";
import { finishTransactionDetailPageQuery } from "./__generated__/finishTransactionDetailPageQuery.graphql";

const query = graphql`
	query finishTransactionDetailPageQuery($id: UUID!) {
		instantBuy(by: { id: $id }) {
			id
			...transactionDetailCardVendingTransaction
			user {
				id
				...userDetailCardUser
			}
			hub {
				id
				meta @required(action: THROW) {
					id
					name
					street
					city
					country
					postalCode
					organization {
						logo {
							variant(variant: PUBLIC)
						}
					}
				}
			}
			lineItems {
				id
				...financialDetailCardLineItems
				productInstance {
					id
					reservation {
						space {
							frame
							position
						}
					}
					hubProduct {
						id
						product {
							id
							name
							description
							brand {
								id
								name
							}
							photo {
								variant(variant: PUBLIC)
							}
							...productDetailCardProduct
						}
					}
				}
				priceAmount
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/pick-up/finish")({
	beforeLoad: () => {
		document.title = "bacehub | Startseite";
	},
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			hubProductId: z.string().optional(),
			transactionId: z.string().optional(),
		})
	),
	loaderDeps: ({ search }) => ({
		transactionId: search.transactionId,
	}),
	loader: async ({ context, deps }) => {
		if (!deps.transactionId) {
			return { transactionQueryRef: null };
		}

		const transactionQueryRef = loadQuery<finishTransactionDetailPageQuery>(context.relayEnvironment, query, { id: deps.transactionId });

		return { transactionQueryRef };
	},
});

export default function RouteComponent() {
	const { hubProductId } = Route.useSearch();
	const { hubMeta } = usePreloadedQuery<routeHubMetaNameQuery>(hubMetaNameQuery, useLoaderData({ from: "/$hubMetaName" }));
	const { transactionQueryRef } = Route.useLoaderData();

	const transactionData = transactionQueryRef ? usePreloadedQuery<finishTransactionDetailPageQuery>(query, transactionQueryRef) : null;
	const { hub } = useHubSession();
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const useCartStore = useCart();

	useCartStore.getState().clearCart();

	const selectedProduct = hubMeta.hub.products?.nodes?.find((p) => p.id === hubProductId);
	const instantBuy = transactionData?.instantBuy;
	const lineItems = instantBuy?.lineItems ?? [];
	const total = lineItems.reduce((sum, item) => sum + (item.priceAmount ?? 0), 0);
	const transactionHubMeta = instantBuy?.hub?.meta;
	const orderImage = transactionHubMeta?.organization.logo?.variant ?? "https://ui.shadcn.com/placeholder.svg";
	const locationName = transactionHubMeta?.name ?? hubMeta.name;
	const locationStreet = transactionHubMeta?.street ?? hubMeta.street;
	const locationPostalCode = transactionHubMeta?.postalCode ?? hubMeta.postalCode;
	const locationCity = transactionHubMeta?.city ?? hubMeta.city;

	const handleProductClick = (hubProductId: string) => {
		navigate({
			to: "/$hubMetaName/pick-up/finish",
			params: { hubMetaName: hubMeta.name },
			search: { hubProductId },
		});
	};

	return (
		<>
			<PageContainer
				footer={
					<Button size="lg" className="w-full" asChild>
						<Link
							to="/"
							search={{
								hubMetaName: hubMeta.name,
								side: hub?.side as "A" | "B" | undefined,
							}}
						>
							<T>{i18n.BACK_TO_HOME()}</T>
						</Link>
					</Button>
				}
				className="bg-muted"
			>
				<AppHeader service="ALL" />
				<div className="px-6 pt-4">
					<h1 className="text-2xl font-breit">
						<T>{i18n.ALL_DONE()}</T>
					</h1>
					<h1 className="text-2xl font-breit">
						<T>{i18n.THANK_YOU_BACE()}</T>
					</h1>
				</div>

				{instantBuy && (
					<div className="bg-muted py-4 flex flex-col gap-4">
						<p className="text-sm font-medium font-breit px-6">
							<T>{i18n.YOUR_ORDER()}</T>
						</p>

						<div className="px-6 pb-2">
							<img src={orderImage} alt="" className="w-full h-20 object-contain bg-muted/30" />
						</div>

						<div className="px-6">
							<p className="text-sm font-medium font-breit mb-2">
								<T>{i18n.LOCATION()}</T>
							</p>
							<EmptyCard3
								icon={MapPin}
								title={locationName}
								description={
									<>
										{locationStreet}
										<br />
										{locationPostalCode} {locationCity}
									</>
								}
							/>
						</div>

						<div className="px-6">
							{lineItems.map((lineItem) => {
								const product = lineItem.productInstance.hubProduct.product;
								return (
									<div key={lineItem.id} className="border-b border-border py-5">
										<div className="flex gap-4 items-start">
											<img
												src={product.photo?.variant ?? "https://ui.shadcn.com/placeholder.svg"}
												alt=""
												className="size-[52px] rounded-md object-cover"
											/>
											<div className="flex-1 min-w-0">
												<p className="text-xs text-muted-foreground leading-4">{product.brand?.name}</p>
												<div className="flex justify-between gap-4">
													<p className="text-sm font-breit">{product.name}</p>
													<p className="text-sm font-breit shrink-0">{formatToCurrency(lineItem.priceAmount ?? 0)}</p>
												</div>
												<p className="text-xs text-muted-foreground leading-4">{product.description}</p>
											</div>
										</div>
									</div>
								);
							})}
						</div>

						<div className="px-6">
							<p className="text-sm font-medium font-breit flex justify-between">
								<span>
									<T>{i18n.TOTAL()}</T>
								</span>
								<span>{formatToCurrency(total)}</span>
							</p>
						</div>

						{hubMeta.organization?.supportEmail && (
							<p className="text-sm text-muted-foreground px-6">{i18n.SELLER_SUPPORT_TEXT({ email: hubMeta.organization.supportEmail })}</p>
						)}
					</div>
				)}
				<RecommendedProducts
					hubId={hubMeta.hub.id}
					showAddButton={false}
					hideOutOfStock={true}
					onProductClick={handleProductClick}
					showSectionTitle={false}
					title={i18n.MORE_PRODUCTS()}
				/>
			</PageContainer>
			{selectedProduct && (
				<ProductDrawer
					key={hubProductId}
					hubId={hubMeta.hub.id}
					product={selectedProduct}
					onClose={() => {
						navigate({
							to: "/$hubMetaName/pick-up/finish",
							params: { hubMetaName: hubMeta.name },
							search: { hubProductId: undefined },
							replace: true,
						});
					}}
				/>
			)}
		</>
	);
}
